import { ADMIN_ROUTE_PREFIX } from '@/constants/security';
import { authDebug } from '@/lib/authDebugInstrumentation';

let registrationPromise: Promise<ServiceWorkerRegistration> | null = null;
let runtimeConfigSyncInstalled = false;

function canUseServiceWorker(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  if (!('serviceWorker' in navigator)) {
    return false;
  }

  return window.isSecureContext || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
}

function syncRuntimeConfig(registration: ServiceWorkerRegistration): void {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }

  const message = {
    type: 'rc-sw-config',
    payload: {
      adminRoutePrefix: ADMIN_ROUTE_PREFIX,
    },
  };

  const candidates = new Set<ServiceWorker>();
  const pushCandidate = (worker: ServiceWorker | null | undefined) => {
    if (worker) {
      candidates.add(worker);
    }
  };

  pushCandidate(registration.active);
  pushCandidate(registration.waiting);
  pushCandidate(registration.installing);
  pushCandidate(navigator.serviceWorker.controller);

  candidates.forEach((worker) => {
    try {
      worker.postMessage(message);
    } catch {
      // Ignore transient worker lifecycle races.
    }
  });
}

function installRuntimeConfigSyncListener(): void {
  if (runtimeConfigSyncInstalled || typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }

  runtimeConfigSyncInstalled = true;

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    authDebug('ServiceWorker.controllerchange', {
      hasController: Boolean(navigator.serviceWorker.controller),
    });
    void navigator.serviceWorker.ready.then((registration) => {
      syncRuntimeConfig(registration);
    }).catch(() => {});
  });
}

export function registerAppServiceWorker(): Promise<ServiceWorkerRegistration> {
  if (!canUseServiceWorker()) {
    return Promise.reject(new Error('service_worker_unavailable'));
  }

  installRuntimeConfigSyncListener();

  if (!registrationPromise) {
    authDebug('ServiceWorker.register.start', { path: '/sw.js' });
    registrationPromise = navigator.serviceWorker.register('/sw.js').then((registration) => {
      authDebug('ServiceWorker.register.done', {
        hasActive: Boolean(registration.active),
        hasWaiting: Boolean(registration.waiting),
        hasInstalling: Boolean(registration.installing),
      });
      syncRuntimeConfig(registration);
      return registration;
    }).catch((error) => {
      authDebug('ServiceWorker.register.error', {
        message: error instanceof Error ? error.message : String(error),
      });
      throw error;
    });
  }

  return registrationPromise.then((registration) => {
    syncRuntimeConfig(registration);
    return registration;
  });
}

export async function getReadyAppServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
  await registerAppServiceWorker();

  const readyRegistration = await navigator.serviceWorker.ready;
  if (!readyRegistration.active) {
    throw new Error('service_worker_not_active');
  }

  syncRuntimeConfig(readyRegistration);

  return readyRegistration;
}

export function isAppServiceWorkerSupported(): boolean {
  return canUseServiceWorker();
}